import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  Timestamp,
  where,
} from '@angular/fire/firestore';
import { QueryConstraint } from 'firebase/firestore';
import { Observable, map, catchError, of } from 'rxjs';
import { dbConst } from './databaseConstants';
import { ISupportMessage } from 'app/models/support';

@Injectable({ providedIn: 'root' })
export class SupportMessageService {
  constructor(private firestore: Firestore) {}

  private collectionPath(threadId: string): string {
    return `${dbConst.supportThreads}/${threadId}/${dbConst.supportMessages}`;
  }

  getMessages(
    threadId: string,
    includeInternal: boolean = false,
    take: number = 200,
  ): Observable<ISupportMessage[]> {
    const listRef = collection(this.firestore, this.collectionPath(threadId));
    const constraints: QueryConstraint[] = [];
    if (!includeInternal) {
      constraints.push(where('internal', '==', false));
    }
    constraints.push(orderBy('createdAt', 'asc'));
    constraints.push(limit(take));
    const q = query(listRef, ...constraints);
    return collectionData(q, { idField: 'id' }).pipe(
      map((items) => items.map((it) => this.convertTimestamps(it as any))),
      catchError((err) => {
        console.error('getMessages failed', err);
        return of([] as ISupportMessage[]);
      }),
    ) as Observable<ISupportMessage[]>;
  }

  getNewId(threadId: string): string {
    const collRef = collection(this.firestore, this.collectionPath(threadId));
    return doc(collRef).id;
  }

  async addMessage(threadId: string, message: ISupportMessage): Promise<ISupportMessage> {
    const id = message.id ?? this.getNewId(threadId);
    const docRef = doc(this.firestore, `${this.collectionPath(threadId)}/${id}`);
    const payload: ISupportMessage = {
      ...message,
      id,
      threadId,
      createdAt: message.createdAt ?? new Date(),
    };
    await setDoc(docRef, payload);
    return payload;
  }

  async deleteMessage(threadId: string, messageId: string): Promise<void> {
    const docRef = doc(this.firestore, `${this.collectionPath(threadId)}/${messageId}`);
    await deleteDoc(docRef);
  }

  async deleteAllMessages(threadId: string): Promise<void> {
    const listRef = collection(this.firestore, this.collectionPath(threadId));
    const snapshot = await getDocs(listRef);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  }

  async markRead(threadId: string, messageId: string, role: 'user' | 'developer'): Promise<void> {
    const docRef = doc(this.firestore, `${this.collectionPath(threadId)}/${messageId}`);
    const patch = role === 'user' ? { readByUser: new Date() } : { readByDev: new Date() };
    await updateDoc(docRef, patch);
  }

  private convertTimestamps(obj: any): ISupportMessage {
    if (!obj) return obj;
    const out: any = { ...obj };
    for (const key of Object.keys(out)) {
      const v = out[key];
      if (v instanceof Timestamp) {
        out[key] = v.toDate();
      } else if (v && typeof v === 'object' && typeof v.seconds === 'number' && typeof v.nanoseconds === 'number') {
        out[key] = new Timestamp(v.seconds, v.nanoseconds).toDate();
      }
    }
    return out as ISupportMessage;
  }
}
